import { DateTime } from 'luxon'

import { ZoneResponseDTO } from '@core/api/dtos/response/zone/zone'
import { Zone } from '@core/api/entities/zone/zone'
import { AudioVersionMapper } from '@core/api/mappers/audioVersionMapper'
import { BoxAllocationMapper } from '@core/api/mappers/boxAllocationMapper'
import { GenericMapper } from '@core/api/mappers/genericMapper'
import { GroupMapper } from '@core/api/mappers/groupMapper'
import { LocationMapper } from '@core/api/mappers/locationMapper'
import { SongMapper } from '@core/api/mappers/song/songMapper'
import { FlagMapper } from '@core/api/mappers/zone/flagMapper'
import { StateMapper } from '@core/api/mappers/zone/stateMapper'

import { ScheduleMapper } from '../scheduleMapper'

export class ZoneMapper {
	private static mapFlags(
		flags: ZoneResponseDTO['flags'] | undefined
	): string[] {
		if (!flags) return []
		if (Array.isArray(flags)) {
			return flags.filter((flag): flag is string => typeof flag === 'string')
		}
		if (Array.isArray(flags.data)) {
			return FlagMapper.toKeys(flags.data)
		}
		return []
	}

	static toEntity = (dto: ZoneResponseDTO): Zone => {
		const dates = GenericMapper.mapDates({
			createdAt: dto.created_at,
			updatedAt: dto.updated_at,
		})

		return new Zone({
			id: dto.id,
			accountId: dto.account_id,
			accountName: dto.account_name,
			code: dto.code,
			uuid: dto.uuid,
			name: dto.name,
			storeId: dto.store_id,
			storageInfo: dto.storage_info,
			volume: dto.volume,
			boxVolume: dto.box_volume,
			hasVolumeChangePending: dto.has_volume_change_pending,
			sendLogs: dto.send_logs,
			resetState: dto.reset_state,
			isSmartVolumeEnabled: dto.is_smart_volume_enabled,
			smartIntegrationIds: dto.smart_integration_ids,
			integrationSource: dto.integration_source,
			lastSmartUpdateAt: dto.last_smart_update_at
				? DateTime.fromISO(dto.last_smart_update_at)
				: null,
			suspended: dto.suspended,
			advertAudioVersionId: dto.advert_audio_version_id,
			flags: ZoneMapper.mapFlags(dto.flags),
			createdAt: dates.createdAt ?? DateTime.fromISO(dto.created_at),
			updatedAt: dates.updatedAt ?? DateTime.fromISO(dto.updated_at),

			groups: dto.groups?.data.map((group) => GroupMapper.toEntity(group)),
			boxAllocations: dto.box_allocations?.data.map((boxAllocation) =>
				BoxAllocationMapper.toEntity(boxAllocation)
			),
			location: dto.store?.data
				? LocationMapper.toEntity(dto.store.data)
				: null,
			state: dto.state?.data ? StateMapper.toEntity(dto.state.data) : null,
			lastPlayedSong: dto.last_played_song?.data
				? SongMapper.toEntity(dto.last_played_song.data)
				: null,
			lastPlayedSchedule: dto.last_played_schedule?.data
				? ScheduleMapper.toEntity(dto.last_played_schedule.data)
				: null,
			advertAudioVersion: dto.advert_audio_version?.data
				? AudioVersionMapper.toEntity(dto.advert_audio_version.data)
				: null,
		})
	}

	static toEntities = (dtos: ZoneResponseDTO[]): Zone[] => {
		return dtos.map((dto) => ZoneMapper.toEntity(dto))
	}
}
